// src/utils/uniqueUserId.ts
import { v4 as uuidv4 } from 'uuid';
import logger from './logger';

// Define a key for storing the ID in localStorage
const USER_ID_KEY = 'deriq_explorer_unique_user_id';

// Cache the ID in memory for the current session to avoid repeated localStorage access
let uniqueUserId: string | null = null;

/**
 * Retrieves the unique user ID from localStorage.
 * If it doesn't exist, generates a new UUID v4, stores it, and returns it.
 * Handles potential localStorage access errors.
 * @returns {string} The unique user ID.
 */
export const getUniqueUserId = (): string => {
  // 1. Return from memory cache if available
  if (uniqueUserId) {
    return uniqueUserId;
  }

  // 2. Try to retrieve from localStorage
  try {
    let storedId = localStorage.getItem(USER_ID_KEY);

    if (!storedId) {
      // 3. Generate a new ID if not found in storage
      storedId = uuidv4();
      localStorage.setItem(USER_ID_KEY, storedId);
      logger.info('Generated and stored new unique user ID:', storedId);
    } else {
      // Optional: Log retrieval for debugging
      logger.debug('Retrieved existing unique user ID:', storedId);
    }

    // 4. Cache in memory and return
    // ToDo: what if generated storedId is null?
    uniqueUserId = storedId;
    return uniqueUserId;

  } catch (error) {
    // 5. Handle potential localStorage errors (e.g., private browsing, storage full)
    logger.error('Error accessing localStorage for unique user ID:', error);

    // Fallback: Generate an ID for this session only (won't persist)
    // If an ID was somehow generated before the error, reuse it.
    if (!uniqueUserId) {
        uniqueUserId = uuidv4();
        logger.warn('Using non-persistent unique user ID for this session due to localStorage error:', uniqueUserId);
    }
    return uniqueUserId;
  }
};

/**
 * Optional: Clears the unique user ID from localStorage and memory cache.
 * Useful for testing or debugging purposes.
 */
export const clearUniqueUserId = (): void => {
    try {
        localStorage.removeItem(USER_ID_KEY);
        uniqueUserId = null; // Clear memory cache
        logger.info('Cleared unique user ID from localStorage and memory.');
    } catch (error) {
        logger.error('Error clearing unique user ID from localStorage:', error);
    }
}
